import React from 'react';
import { useGlobal } from '@rcg/ui-react';
import clsx from 'clsx';
import { useReactiveVar } from '@apollo/client';
import { formatPrice } from '@rcg/pricing-format';
import ScopedCssBaseline from '@material-ui/core/ScopedCssBaseline';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Dictionary from '../../utils/dictionary';
import useStyles from './useStyles';
import TimeDuration, { timeWithLocale } from '../../utils/timeFormats';
import {
  bookingProfileVar,
  selectedDepartureVar,
} from '../../operations/localState/reactiveVariables';
import listsFormats from '../../utils/listsFormats';
import layoverFormats from '../../utils/layoverFormats';
import {
  FLIGHT_SEARCH_TYPE,
  SESSION_STORAGE_CURRENT_BOOKING,
  SESSION_STORAGE_IS_REFUND,
  SESSION_STORAGE_TOTAL_GUEST_SELECTED_FLIGHT,
  NAVIGATION_PATHS,
  SESSION_STORAGE_CURRENT_EXTRADATA_FLIGHT,
} from '../../utils/constants';
import ReviewInfo from '../ReviewInfo';
import useDictionary from '../../hooks/useDictionary';
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from '../../operations/sessionStorage/utils';

const setClasses = (classes, panelClass, displayDesktopBanner, subTitle) => {
  const displayDesktopBannerClass = displayDesktopBanner ? classes.withBanner : classes.cardHeader;
  const headerClass = !panelClass
    ? (classes.cardHeader, displayDesktopBannerClass)
    : classes.cardHeaderPanel;
  const isMarginBottom = !subTitle ? classes.cardTitleNoMarginBottom : '';
  const cardTittleClass = !panelClass ? classes.cardTitle : classes.cardTitlePanel;
  const cardSubtitleClass = !panelClass ? classes.cardSubTitle : classes.cardSubTitlePanel;
  const cardFlightSummaryClass = !panelClass
    ? classes.cardFlightSummary
    : classes.cardFlightSummaryPanel;
  const cardFlightSummaryWrapperClass = !panelClass
    ? classes.cardFlightSummaryWrapper
    : classes.cardFlightSummaryWrapperPanel;

  return {
    headerClass,
    cardTittleClass,
    cardSubtitleClass,
    cardFlightSummaryClass,
    cardFlightSummaryWrapperClass,
    isMarginBottom,
  };
};

const ReviewCard = ({
  title,
  subTitle,
  data,
  extraData,
  isBooked = false,
  balanceLabel,
  displayDesktopBanner,
  panelClass,
  isChangeFlights,
  isDesktop,
  isSummary = false,
  cardTitle,
  summaryBalance = false,
  setTotalBalance = () => {},
  setIsRefund = () => {},
  handleAccordion,
  isNewDeparting = false,
  confirmationNumbers = null,
  payNowSelected = null,
  mockIsConfirmationPage = false,
  isOneWay = false,
  isChange,
}) => {
  const classes = useStyles();
  const getDictionaryValue = useDictionary(Dictionary);
  const {
    renderContent,
    data: { localizedDayjs },
  } = useGlobal();

  const selectedDeparture = useReactiveVar(selectedDepartureVar);

  const { inboundFlight, outboundFlight, priceChange } = isNewDeparting
    ? {
        outboundFlight: selectedDeparture?.flight,
        inboundFlight: selectedDeparture?.flight,
      }
    : {
        outboundFlight: data.outboundFlight || data.inboundFlight,
        inboundFlight: data.inboundFlight || data.outboundFlight,
        priceChange: data.priceChange,
      };
  const { departureFlight, returnFlight } = isNewDeparting
    ? {
        departureFlight: {
          cabin: selectedDeparture?.cabin,
          flight: selectedDeparture?.flight,
        },
        returnFlight: {
          cabin: selectedDeparture?.cabin,
          flight: selectedDeparture?.flight,
        },
      }
    : {
        departureFlight: {
          cabin: extraData.departureFlight?.cabin || extraData.returnFlight?.cabin,
          flight: extraData.departureFlight?.flight || extraData.returnFlight?.flight,
        },
        returnFlight: {
          cabin: extraData.returnFlight?.cabin || extraData.departureFlight?.cabin,
          flight: extraData.returnFlight?.flight || extraData.departureFlight?.flight,
        },
      };

  const bookingProfile = useReactiveVar(bookingProfileVar);

  const currentBooking = getSessionStorageItem(SESSION_STORAGE_CURRENT_BOOKING);
  const extraDataChangeFlight = getSessionStorageItem(SESSION_STORAGE_CURRENT_EXTRADATA_FLIGHT);
  // outboud flight info
  const outboundFlightFirstSegment = outboundFlight.flightSegments[0];
  const outboundFlightSegmentsCount = outboundFlight.flightSegments.length - 1;
  const outboundFlightLastSegment = outboundFlight.flightSegments[outboundFlightSegmentsCount];
  const outboundFlightDate = localizedDayjs(outboundFlightFirstSegment.departure.date).format(
    'ddd[,] MMM D[,] YYYY',
  );

  const outboundFlightStartTime = timeWithLocale(
    localizedDayjs,
    outboundFlightFirstSegment.departure.time,
  );
  const outboundFlightEndTime = timeWithLocale(
    localizedDayjs,
    outboundFlightLastSegment.arrival.time,
  );

  const outboundFlightDepartureCity = outboundFlightFirstSegment.departure.airport.code;
  const outboundFlightArrivalCity = outboundFlightLastSegment.arrival.airport.code;
  const outboundFlightTotalDuration = TimeDuration(outboundFlight.totalDurationMins);
  const outboundFlightNumber = `${outboundFlightFirstSegment.marketingAirline.code} ${outboundFlightFirstSegment.number}`;
  const outboundFlightNumberOfStops = listsFormats.numberOfStops(
    outboundFlight.flightSegments,
    renderContent,
  );
  const outboundFlightAirlines = listsFormats.listOfAirlines(
    listsFormats.marketingAirlines(departureFlight.flight?.flightSegments),
  );

  const totalGuestSelected = getSessionStorageItem(SESSION_STORAGE_TOTAL_GUEST_SELECTED_FLIGHT);
  const outboundFlightTotalPassangers = totalGuestSelected || departureFlight.cabin.passengerQty;
  // layoverMins of the first segment
  const outboundFlightCountStopsLabelBig = listsFormats.countStopsLabelBig(outboundFlight);
  const outboundFlightCountStopsLabel = listsFormats.countStopsLabel(
    departureFlight?.flight?.flightSegments || returnFlight?.flight?.flightSegments,
    getDictionaryValue(Dictionary.airLabelInForStops.key),
  );
  const outboundFlightLayoverDays = layoverFormats.layoverDays({
    departure: departureFlight.flight.departDate,
    arrival: outboundFlightLastSegment.arrival.date,
  });
  const outboundFlightClass = departureFlight.cabin.cabinClass.code;
  // inbound flight info
  const inboundFlightFirstSegment = inboundFlight?.flightSegments[0];
  const inboundFlightSegmentsCount = inboundFlight.flightSegments.length - 1;
  const inboundFlightLastSegment = inboundFlight?.flightSegments[inboundFlightSegmentsCount];
  const inboundFlightDate = localizedDayjs(inboundFlightFirstSegment?.departure.date).format(
    'ddd[,] MMM D[,] YYYY',
  );
  const inboundFlightStartTime = timeWithLocale(
    localizedDayjs,
    inboundFlightFirstSegment?.departure.time,
  );
  const inboundFlightEndTime = timeWithLocale(
    localizedDayjs,
    inboundFlightLastSegment?.arrival.time,
  );
  const inboundFlightDepartureCity = inboundFlightFirstSegment?.departure.airport.code;
  const inboundFlightArrivalCity = inboundFlightLastSegment?.arrival.airport.code;
  const inboundFlightTotalDuration = TimeDuration(inboundFlight?.totalDurationMins);
  const inboundFlightNumber = `${inboundFlightFirstSegment?.marketingAirline.code} ${inboundFlightFirstSegment?.number}`;
  const inboundFlightNumberOfStops = listsFormats.numberOfStops(
    inboundFlight?.flightSegments,
    renderContent,
  );
  const inboundFlightAirlines = listsFormats.listOfAirlines(
    listsFormats.marketingAirlines(returnFlight?.flight?.flightSegments),
  );
  const inboundFlightTotalPassangers = totalGuestSelected || returnFlight?.cabin?.passengerQty;
  const inboundFlightCountStopsLabelBig = listsFormats.countStopsLabelBig(inboundFlight);
  const inboundFlightCountStopsLabel = listsFormats.countStopsLabel(
    returnFlight?.flight?.flightSegments || departureFlight?.flight?.flightSegments,
  );
  const inboundFlightLayoverDays = layoverFormats.layoverDays({
    departure: returnFlight.flight.departDate,
    arrival: inboundFlightLastSegment.arrival.date,
  });
  const inboundFlightClass = returnFlight.cabin.cabinClass.code;
  // summary info
  const roundtripTickets = outboundFlightTotalPassangers;
  const currentCurrency =
    bookingProfile?.flightProfile?.currency || returnFlight?.cabin?.totalPrice?.currency;

  const dueDate = localizedDayjs(bookingProfile?.flightProfile?.booking?.finalPaymentDate).format(
    'MMM D[,] YYYY',
  );

  const currentUrl = window.location.href;
  const urlSegments = currentUrl.split('/');
  const isConfirmationPage = mockIsConfirmationPage
    ? true
    : `/${urlSegments[urlSegments.length - 1]}` === NAVIGATION_PATHS.confirmation;

  const pricePerPerson = priceChange
    ? formatPrice(priceChange?.pricePerPassenger?.value, currentCurrency, true, true, false)
    : formatPrice(
        returnFlight?.cabin?.pricePerPassenger?.value ||
          departureFlight?.cabin?.pricePerPassenger?.value,
        currentCurrency,
        true,
        true,
        false,
      );
  let totalPrice = !isBooked
    ? formatPrice(returnFlight?.cabin?.totalPrice?.value, currentCurrency, true, true, true)
    : formatPrice(0, currentCurrency, true, true, true);
  totalPrice =
    priceChange && !isBooked
      ? formatPrice(priceChange?.newTotal?.value, currentCurrency, true, true, false)
      : totalPrice;

  const reviewCardClass = setClasses(classes, panelClass, displayDesktopBanner, subTitle);

  const balanceSummary = {
    totalFare: priceChange ? priceChange?.newTotal.value : returnFlight?.cabin?.totalPrice?.value,
    cancellationFee: currentBooking
      ? roundtripTickets * (currentBooking?.flightProfile?.guests[0]?.flightGuest?.airFee || 0)
      : roundtripTickets * (bookingProfile?.flightProfile?.guests[0]?.flightGuest?.airFee || 0),
    refound: extraDataChangeFlight?.returnFlight?.cabin?.totalPrice.value || 0,
    get balance() {
      const totalBalance = isChangeFlights
        ? balanceSummary.totalFare + balanceSummary.cancellationFee - balanceSummary.refound
        : balanceSummary.totalFare;
      setTotalBalance(totalBalance);
      return totalBalance;
    },
  };

  const balanceAmount =
    priceChange && !isBooked && !isChangeFlights
      ? formatPrice(priceChange?.newTotal?.value, currentCurrency, true, true, false)
      : formatPrice(Math.abs(balanceSummary.balance), currentCurrency, true, true, true);

  const getBalanceLabel = () => {
    if (balanceSummary.balance < 0 && isChangeFlights) {
      setIsRefund(true);
      setSessionStorageItem(SESSION_STORAGE_IS_REFUND, true);
      return getDictionaryValue(Dictionary.airFlightSummaryBalanceRefundTotalLabel.key);
    }
    if (balanceLabel) {
      if (balanceSummary.balance === 0) {
        setIsRefund(true);
      }
      return getDictionaryValue(Dictionary.airPaymentReviewAndPayBalanceLabel.key);
    }
    return getDictionaryValue(Dictionary.airPaymentReviewAndPaySubTotalPriceLabel.key);
  };

  const getTotalPrice = (flight) => {
    return priceChange ? priceChange.newTotal : flight?.cabin?.totalPrice;
  };

  const { value, currency } = isNewDeparting
    ? getTotalPrice(returnFlight, priceChange) || getTotalPrice(departureFlight, priceChange)
    : getTotalPrice(extraData?.returnFlight, priceChange) ||
      getTotalPrice(extraData?.departureFlight, priceChange);

  const totalFlightPrice = formatPrice(value, currency, true, true, true);

  const flightTypeLabel = () => {
    const oneWayLabel = getDictionaryValue(
      roundtripTickets === 1
        ? Dictionary.airOnewayTicketLabel.key
        : Dictionary.airOnewayTicketsLabel.key,
    );

    const roundTripLabel = getDictionaryValue(
      roundtripTickets === 1
        ? Dictionary.airRoundtripTicketLabel.key
        : Dictionary.airRoundtripTicketsLabel.key,
    );

    return isOneWay ? oneWayLabel : roundTripLabel;
  };

  const renderFlightInfoBlock = (
    <div className={!panelClass ? classes.cardContainerNoShadow : classes.cardContainerPanel}>
      <div
        className={
          !panelClass
            ? clsx(classes.cardFlightInfo, {
                [classes.changeFlightsView]: isChangeFlights && !isSummary,
              })
            : ''
        }>
        <ReviewInfo
          panelClass={panelClass}
          titlePreDate={getDictionaryValue(Dictionary.airDepartingLabel.key)}
          flightIcon={outboundFlight.flightIcon}
          date={outboundFlightDate}
          startTime={outboundFlightStartTime}
          endTime={outboundFlightEndTime}
          departureCity={outboundFlightDepartureCity}
          arrivalCity={outboundFlightArrivalCity}
          totalDuration={outboundFlightTotalDuration}
          numberOfStops={outboundFlightNumberOfStops}
          airlines={outboundFlightAirlines}
          flightClass={outboundFlightClass}
          flightNumber={outboundFlightNumber}
          firstSegment={outboundFlightFirstSegment}
          numberOfPassangers={outboundFlightTotalPassangers}
          countStopsLabel={outboundFlightCountStopsLabel}
          countStopsLabelBig={outboundFlightCountStopsLabelBig}
          layoverDays={outboundFlightLayoverDays}
          flightInfo={departureFlight}
          flightType={{ type: FLIGHT_SEARCH_TYPE.DEPARTURE }}
          isChangeFlights={isChangeFlights}
          confirmationNumbers={confirmationNumbers?.departing}
          isRefundable={departureFlight?.cabin?.isRefundable}
          isNewDeparting={isNewDeparting}
          isChange={isChange}
        />
        {!isNewDeparting && !isOneWay && (
          <>
            <ReviewInfo
              panelClass={panelClass}
              titlePreDate={getDictionaryValue(Dictionary.airReturningLabel.key)}
              flightIcon={inboundFlight.flightIcon}
              date={inboundFlightDate}
              startTime={inboundFlightStartTime}
              endTime={inboundFlightEndTime}
              departureCity={inboundFlightDepartureCity}
              arrivalCity={inboundFlightArrivalCity}
              totalDuration={inboundFlightTotalDuration}
              numberOfStops={inboundFlightNumberOfStops}
              airlines={inboundFlightAirlines}
              flightClass={inboundFlightClass}
              flightNumber={inboundFlightNumber}
              firstSegment={inboundFlightFirstSegment}
              numberOfPassangers={inboundFlightTotalPassangers}
              countStopsLabel={inboundFlightCountStopsLabel}
              countStopsLabelBig={inboundFlightCountStopsLabelBig}
              layoverDays={inboundFlightLayoverDays}
              flightInfo={returnFlight}
              flightType={{ type: FLIGHT_SEARCH_TYPE.RETURN }}
              isChangeFlights={isChangeFlights}
              isSummary={isSummary}
              confirmationNumbers={confirmationNumbers?.returning}
              isRefundable={returnFlight?.cabin?.isRefundable}
              isNewDeparting={isNewDeparting}
              isChange={isChange}
            />
          </>
        )}
      </div>
      {isSummary && (
        <div className={reviewCardClass.cardFlightSummaryClass}>
          <div className={reviewCardClass.cardFlightSummaryWrapperClass}>
            <div className={classes.summaryTitle}>
              {getDictionaryValue(Dictionary.airPaymentReviewAndPaySubSummaryLabel.key)}
            </div>
            <div className={classes.summaryTicket}>
              <div> {flightTypeLabel()} </div>
              <div>
                {pricePerPerson}/{getDictionaryValue(Dictionary.airPersonLabel.key)}
              </div>
              {summaryBalance && isChangeFlights ? (
                <>
                  <div
                    style={{
                      borderTop: '1px solid lightgray',
                      fontWeight: 600,
                    }}>
                    {getDictionaryValue(Dictionary.airFlightSummaryBalanceTotalFareLabel.key)}
                  </div>
                  <div
                    style={{
                      borderTop: '1px solid lightgray',
                      fontWeight: 600,
                    }}>
                    {totalPrice}
                  </div>
                  <div>
                    {getDictionaryValue(Dictionary.airFlightSummaryBalanceCancellationFeeLabel.key)}
                  </div>
                  <div>
                    {formatPrice(
                      balanceSummary.cancellationFee || 0,
                      currentCurrency,
                      true,
                      true,
                      true,
                    )}
                  </div>
                  <div>
                    {getDictionaryValue(Dictionary.airFlightSummaryBalanceRefoundLabel.key)}
                  </div>
                  <div>
                    - {formatPrice(balanceSummary.refound, currentCurrency, true, true, true)}
                  </div>
                </>
              ) : (
                isConfirmationPage && (
                  <>
                    <div
                      style={{
                        borderTop: '1px solid lightgray',
                        fontWeight: 600,
                      }}>
                      {getDictionaryValue(Dictionary.airFlightSummaryBalanceTotalFareLabel.key)}
                    </div>
                    <div
                      style={{
                        borderTop: '1px solid lightgray',
                        fontWeight: 600,
                      }}>
                      {formatPrice(balanceSummary.totalFare, currentCurrency, true, true, true)}
                    </div>
                    <div className={classes.summarySpacing}>
                      {getDictionaryValue(Dictionary.airConfirmationSummaryPaidToday.key)}
                    </div>
                    <div className={classes.summarySpacing}>
                      {payNowSelected
                        ? `-${formatPrice(
                            balanceSummary.totalFare,
                            currentCurrency,
                            true,
                            true,
                            true,
                          )}`
                        : formatPrice(0, currentCurrency, true, true, true)}
                    </div>
                    {isChangeFlights && (
                      <>
                        <div>
                          {getDictionaryValue(
                            Dictionary.airFlightSummaryBalanceCancellationFeeLabel.key,
                          )}
                        </div>
                        <div>
                          {formatPrice(
                            balanceSummary.cancellationFee || 0,
                            currentCurrency,
                            true,
                            true,
                            true,
                          )}
                        </div>
                        <div>
                          {getDictionaryValue(Dictionary.airFlightSummaryBalanceRefoundLabel.key)}
                        </div>
                        <div>
                          - {formatPrice(balanceSummary.refound, currentCurrency, true, true, true)}
                        </div>
                      </>
                    )}
                  </>
                )
              )}
            </div>
            {!isBooked ? (
              <div className={classes.totalPrice}>
                <div>{getBalanceLabel()}</div>
                <div>{summaryBalance ? balanceAmount : totalFlightPrice}</div>
              </div>
            ) : (
              <div className={classes.totalPrice}>
                <div>{getDictionaryValue(Dictionary.airPaymentReviewAndPayBalanceLabel.key)}</div>
                <div>{totalPrice}</div>
              </div>
            )}
            {dueDate &&
              isConfirmationPage &&
              !payNowSelected &&
              getBalanceLabel() !==
                getDictionaryValue(Dictionary.airFlightSummaryBalanceRefundTotalLabel.key) &&
              balanceAmount.match(/\d+\.\d+/)?.[0] !== '0.00' && (
                <div className={classes.dueDate}>
                  <span>{getDictionaryValue(Dictionary.airConfirmationPaymentDue.key)}</span>
                  <span>{dueDate}</span>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <ScopedCssBaseline>
      {(title || subTitle) && (
        <div className={clsx(reviewCardClass.headerClass)}>
          {title && (
            <div className={clsx(reviewCardClass.cardTittleClass, reviewCardClass.isMarginBottom)}>
              {title}
            </div>
          )}
          {subTitle && <div className={reviewCardClass.cardSubtitleClass}>{subTitle}</div>}
        </div>
      )}
      <Accordion expanded={isDesktop} onChange={handleAccordion}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          className={classes.accordionSummary}>
          <Typography>{cardTitle}</Typography>
          {!isBooked ? (
            <Typography>
              {`${getBalanceLabel()} ${summaryBalance ? balanceAmount : totalFlightPrice}`}
            </Typography>
          ) : (
            <Typography>{`${getBalanceLabel()} ${totalPrice}`}</Typography>
          )}
        </AccordionSummary>
        <AccordionDetails className={classes.centeredAccordionDetails}>
          {renderFlightInfoBlock}
        </AccordionDetails>
      </Accordion>
    </ScopedCssBaseline>
  );
};

export default ReviewCard;
